import styled from 'styled-components'
import { Select } from 'sutra/components/Select/Select'
import { Text } from 'sutra/components/Text/Text'

import {
  cardBorder,
  darkBlueFontColor,
  lightText,
  pageBackgroundGrey,
} from 'assets/styles/variables'

export const pillToolbarSelectCss = `
  && {
    width: 181px;
    height: 36px;
    padding-inline: 12px;
    border-radius: 8px;
    border-color: ${cardBorder};
    background: #fff;
    font-size: var(--Font-size-text-sm, 14px);
    font-weight: 500;
    color: ${darkBlueFontColor};
  }

  &&:hover:not(:disabled),
  &&[data-popup-open] {
    border-color: ${cardBorder};
  }

  && [data-placeholder] {
    color: ${darkBlueFontColor};
  }
`

// `$hidden` keeps a panel MOUNTED but not displayed, so switching subsection
// tabs doesn't unmount (and therefore refetch) the feed behind it.
//
// The hide MUST be the single `display` declaration in this rule, not an extra
// one earlier in the block: a later `display: flex` in the same rule wins over
// an earlier `display: none`, which silently leaves every panel visible at once.
export const FeedContentPanel = styled.div<{
  $flush?: boolean
  $hidden?: boolean
}>`
  background: white;
  border-top: 1px solid #dedee0;
  padding-top: ${({ $flush }) => ($flush ? 0 : '20px')};
  padding-bottom: 40px;
  padding-left: 20px;
  padding-right: 20px;
  margin-left: -20px;
  margin-right: -20px;
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  flex: 1;
  gap: 16px;
`

export const FeedToolbarRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 20px;
  padding-right: 20px;
  border-top: 1px solid #dedee0;
  border-bottom: 1px solid #dedee0;
  background: white;
  margin-left: -20px;
  margin-right: -20px;
  flex-shrink: 0;
`

export const FeedToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const FeedToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const FeedSectionHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const FeedSectionHeading = styled.h2`
  margin: 0;
  font-family: var(--Font-family-font-family-body), sans-serif;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: #14223b;
  letter-spacing: 0;
`

export const FeedSectionSubheading = styled.p`
  margin: 0;
  font-family: var(--Font-family-font-family-body), sans-serif;
  font-size: 14px;
  line-height: 21px;
  font-weight: 500;
  color: #36415385;
  letter-spacing: 0;
`

export const SortSelect = styled(Select)`
  ${pillToolbarSelectCss}
`

export const AgentFilterSelect = styled(Select)`
  ${pillToolbarSelectCss}
`

export const ToolbarSelectPrefixIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${lightText};
  font-size: 14px;
`

export const FeedSkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const FeedSkeletonCard = styled.div`
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid ${cardBorder};
`

export const IssuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const CardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const PaginationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;
`

export const PaginationPageSummary = styled(Text)`
  && {
    color: ${lightText};
    font-size: 13px;
    line-height: 20px;
    white-space: nowrap;
  }
`

export const FeedEmptyStateWrapper = styled.div`
  min-height: 320px;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid ${cardBorder};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const EmptyHint = styled.p`
  color: ${lightText};
  font-size: 14px;
  margin: 0;
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  padding: 32px 24px;
  text-align: center;
`

export const FeedTabsWrapper = styled.div`
  --tabs-accent: #45689b;
  display: flex;
  flex-direction: column;
  flex: 1;

  > * {
    flex: 1;
  }

  /* No divider under the tab bar; FeedToolbarRow's border-top serves the same
     separator role one row down. */
  > * > [role='tablist'] {
    gap: 16px;
    border-bottom: none;
  }

  > * > [role='tablist'] > [role='tab'] {
    padding: 6px 0;
  }

  > * > [role='tabpanel']:not([hidden]) {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`

export const TrackingCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid #b9c7db;
  background: #e5eaf1;
  margin-left: 6px;
  padding: 2px 4px;
  font-family: var(--Font-family-font-family-body), sans-serif;
  font-size: var(--Font-size-text-xs, 12px);
  line-height: var(--Line-height-text-xs, 16px);
  font-weight: 500;
  color: #375d93;
  text-align: center;
  letter-spacing: 0;
`

export const SubsectionSelectorRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-bottom: 16px;
`

export const SubsectionCard = styled.button<{ $active: boolean }>`
  width: 215px;
  height: 69.75px;
  border-radius: 10px;
  padding: 14px 14px 18px 14px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 13px;
  cursor: pointer;
  background: white;
  border: 2px solid ${({ $active }) => ($active ? '#4F72A4' : '#DEDEE0')};
  box-shadow: ${({ $active }) =>
    $active ? '0px 4px 4px 0px rgba(0, 0, 0, 0.11)' : 'none'};
  text-align: left;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: #4f72a4;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.11);
  }
`

export const SubsectionCardTextBlock = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

export const SubsectionCardIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`

export const SubsectionCardTitle = styled.span`
  font-size: var(--Font-size-text-sm, 14px);
  line-height: var(--Line-height-text-sm, 20px);
  font-weight: 600;
  color: #28364f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const SubsectionCardSubtitle = styled.span`
  font-size: var(--Font-size-text-xs, 12px);
  line-height: var(--Line-height-text-xs, 16px);
  font-weight: 500;
  color: #868d98;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ViewPreviousFeedButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 24px 20px;
  border-radius: 8px;
  border: none;
  background: ${pageBackgroundGrey};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f0eeec;
  }
`

export const ViewPreviousFeedTitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: ${darkBlueFontColor};

  .anticon {
    color: ${lightText};
  }
`

export const ViewPreviousFeedSubtext = styled.span`
  font-size: 14px;
  color: ${lightText};
`

export const DiscoverMoreSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

export const DiscoverMoreStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
